import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { AiCredits_WalletResponseDados } from '../contracts/AiCredits_WalletResponse'

// Import side-effect: registers the contract with the responseContract registry.
import '../contracts/AiCredits_WalletResponse'

export interface FranchiseBalance {
  ok: true
  display: string
  micro: number
  franchiseName: string
}

export interface FranchiseBalanceFailed {
  ok: false
}

export type FranchiseBalanceEntry = FranchiseBalance | FranchiseBalanceFailed

/**
 * useFranchiseBalances — fans out GET ai/credits/wallet/{id} for a list of
 * franchise IDs with a concurrency cap of 4.  Per-id failures are tolerated
 * as { ok: false } entries — the batch never rejects.
 *
 * Network calls use config.api.sistemaHost (same host as useCreditWallet),
 * responseContract: 'AiCredits_WalletResponse', skipErrorDisplay: true.
 *
 * The reactive `balances` map is keyed by franchise ID (number).
 */
export function useFranchiseBalances() {
  /** Reactive map: franchise id → balance entry (populated after fetchBalances). */
  const balances = ref<Map<number, FranchiseBalanceEntry>>(new Map())

  function sistemaUrl(path: string): string {
    return `${config.api.sistemaHost}/${path}`
  }

  /**
   * fetchBalances — fans out GET ai/credits/wallet/{id} for each id in `ids`,
   * with at most 4 concurrent in-flight requests.
   * Per-id errors yield { ok: false } in the map (batch never rejects).
   */
  async function fetchBalances(ids: number[]): Promise<void> {
    if (ids.length === 0) return

    const CONCURRENCY = 4
    const result = new Map<number, FranchiseBalanceEntry>()

    // Process in chunks of CONCURRENCY to cap in-flight requests.
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const chunk = ids.slice(i, i + CONCURRENCY)
      const settled = await Promise.allSettled(
        chunk.map(async (id) => {
          const raw = await httpService.get(
            sistemaUrl(`ai/credits/wallet/${id}`),
            { responseContract: 'AiCredits_WalletResponse', skipErrorDisplay: true },
          )
          const resp = raw as { dados: AiCredits_WalletResponseDados }
          return { id, dados: resp.dados }
        }),
      )

      for (let j = 0; j < chunk.length; j++) {
        const id = chunk[j]
        const outcome = settled[j]
        if (outcome.status === 'fulfilled') {
          const { dados } = outcome.value
          result.set(id, {
            ok: true,
            display: dados.balance_display,
            micro: dados.balance_micro,
            franchiseName: dados.franchise_name,
          })
          log('useFranchiseBalances', 'fetchBalances', 'Saldo carregado', {
            id,
            display: dados.balance_display,
          })
        } else {
          result.set(id, { ok: false })
          log('useFranchiseBalances', 'fetchBalances', 'Falha ao carregar saldo', {
            id,
            reason: (outcome.reason as Error)?.message,
          })
        }
      }
    }

    balances.value = result
  }

  return { balances, fetchBalances }
}
